import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Space} from 'antd';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {
  StyledLegalRepresentativeInformation,
  StyledLegalRepresentativeInformationCopyField,
  StyledLegalRepresentativeInformationLinkButton
} from './StyledLegalRepresentativeInformation';
import {StyledKYCModalContentStepTitle} from '../../../../KYCModal/StyledKYCModal';
import LegalRepresentativeInvitationForm from '../../stepsComponents/LegalRepresentativeInvitationForm';
import LegalRepresentativeInformationForm from '../../stepsComponents/LegalRepresentativeInformationForm';
import {alertActions} from '../../../../../../state/actions/alert.actions';
import {bankingActions} from '../../../../../../state/actions/banking.actions';
import {bankingUserTypesConstants} from '../../../../../../constants';
import {cardsHelpers, kycHelpers} from '../../../../../../utils/helpers';
import {useKYCCompanyHeaders} from '../../../../../../hooks';

const formKey = 'lrInformation';

const COPY_TAB = 'copy';
const FORM_TAB = 'form';
const INVITATION_TAB = 'invitation';

const {LEGAL_REPRESENTATIVE} = bankingUserTypesConstants;

const LegalRepresentativeInformation = React.forwardRef(({
  handleUpdateOkEnable,
  handleLoadUser,
  onFinish,

  user,
  ...rest
}, ref) => {
  const dispatch = useDispatch();
  const kycHeaders = useKYCCompanyHeaders();
  const invitedUser = useSelector(state => state.banking.invitedUser);
  const [t] = useTranslation(['main', 'quickStart']);
  // Opens on the form: a rep completing the KYC live on a call fills it in themselves
  // rather than emailing an invitation (H1MVP-4653).
  const [selectedSegment, setSelectedSegment] = useState(FORM_TAB);
  const [prepopulatedUserDetails, setPrepopulatedUserDetails] = useState({});
  const [submitEnabled, setSubmitEnabled] = useState(true);

  const invitedLRRef = useRef(null);
  const informationFormRef = useRef(null);
  const invitationFormRef = useRef(null);

  useEffect(() => {
    handleUpdateOkEnable && handleUpdateOkEnable(submitEnabled);
  }, [submitEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // The form no longer gates submission on the Treezor checkboxes. The rep must not accept terms
  // on the client's behalf, so acceptance moves to the client's own first login (H1MVP-4654) --
  // and removing the checkboxes while leaving this gate would leave OK permanently disabled.
  useEffect(() => {
    const enabled = selectedSegment !== COPY_TAB;
    if (enabled !== submitEnabled) setSubmitEnabled(enabled);
  }, [selectedSegment]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    description,
    title
  } = useMemo(() => kycHelpers.getKYCFormDetails({t, formKey}), [t]);

  const invitationLink = useMemo(() => invitedUser?.key ? kycHelpers.getKYCInvitePageUrl(invitedUser.key) : undefined, [invitedUser]);

  const tabDescription = (tabKey) => t(`quickStart:kyc.steps.${formKey}.tabs.${tabKey}.description`);

  const updatePrepopulatedUserDetails = (user) => {
    setPrepopulatedUserDetails({
      email: user.email,
      firstname: user.firstname,
      lastname: user.lastname
    });
  }

  useEffect(() => {
    if (invitedUser && selectedSegment !== FORM_TAB) setSelectedSegment(FORM_TAB);
  }, [invitedUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user) updatePrepopulatedUserDetails(user);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendInvitation = (fields, successCallback, errorCallback) => {
    dispatch(bankingActions.sendJoinInvitation({
      data: {
        first_name: fields.first_name || fields.firstname,
        last_name: fields.last_name || fields.lastname,
        email: fields.email,
        user_type: LEGAL_REPRESENTATIVE
      },
      successCallback,
      errorCallback: (err) => {
        const response = err.response.data;
        const formErrors = typeof (response.detail) === 'object'
          ? cardsHelpers.getBankingResponseErrorFields(response)
          : [{name: 'email', errors: [response.detail]}];
        errorCallback && errorCallback(formErrors);
      },
      headers: kycHeaders
    }));
  }

  const handleSubmitInvitationForm = (fields, successCallback, errorCallback) => {
    handleSendInvitation(
      fields,
      (user) => {
        successCallback && successCallback();
        handleLoadUser && handleLoadUser();
        updatePrepopulatedUserDetails(user);
      },
      errorCallback
    );
  }

  const handleSubmitInformationForm = (fields, successCallback, errorCallback) => {
    const data = {
      ...fields,
      birthday: fields.birthday.format('YYYY-MM-DD'),
      user_type: LEGAL_REPRESENTATIVE
    }

    const successFunc = (user) => {
      dispatch(bankingActions.createTaxResidence({
        data: {
          ...kycHelpers.getTaxResidenceFormData(data),
          user_id: user.id
        },
        successCallback: () => {
          successCallback && successCallback();
          onFinish && onFinish({user});
          dispatch(bankingActions.getCompanyState({headers: kycHeaders}));
        },
        errorCallback: (response) => {
          const formErrors = kycHelpers.getTaxNumberFormErrors(response, t);
          errorCallback && errorCallback(formErrors);
        },
        headers: kycHeaders
      }));
    }

    const errorFunc = (response) => {
      let formErrors = cardsHelpers.getBankingResponseErrorFields(response, t);
      errorCallback && errorCallback(formErrors);
    }

    const handleUpdateUser = (user) => {
      dispatch(bankingActions.updateUser({
        id: user.id,
        data,
        successCallback: successFunc,
        errorCallback: errorFunc,
        headers: kycHeaders
      }));
    }

    // The invitation creates the LR record before the update runs. If the update then fails
    // (Treezor rejects a zip code, say), the record exists but the `user` prop is still empty, so
    // a retry would invite again and be refused with "already created for this company".
    // Remember the record the invitation made and update that one on every retry.
    const existingUser = user || invitedLRRef.current;
    if (existingUser) {
      handleUpdateUser(existingUser);
    } else {
      handleSendInvitation(
        fields,
        (invitedUser) => {
          invitedLRRef.current = invitedUser;
          handleUpdateUser(invitedUser);
        },
        errorCallback
      );
    }
  }

  const selectedComponent = useMemo(() => {
    const components = {
      'copy': invitationLink
        ? (
          <StyledLegalRepresentativeInformationCopyField
            buttonType='icon'
            onCopy={() => alertActions.success(`${t('copied')}!`)}
            value={invitationLink}
          />
        ) : (
          <StyledLegalRepresentativeInformationLinkButton
            onClick={() => setSelectedSegment('invitation')}
            size='large'
            type='link'
          >
            {tabDescription('shareLink')}
          </StyledLegalRepresentativeInformationLinkButton>
        ),
      'invitation': (
        <LegalRepresentativeInvitationForm
          ref={invitationFormRef}
          onSubmit={handleSubmitInvitationForm}
        />
      ),
      'form': (
        <LegalRepresentativeInformationForm
          defaultFormValues={prepopulatedUserDetails}
          onSubmit={handleSubmitInformationForm}
          ref={informationFormRef}
        />
      ),
    }
    return components[selectedSegment] || undefined;
  }, [selectedSegment, prepopulatedUserDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = () => {
    const actions = {
      [INVITATION_TAB]: () => invitationFormRef.current.submit(),
      [FORM_TAB]: () => informationFormRef.current.submit()
    }
    if (actions.hasOwnProperty(selectedSegment)) actions[selectedSegment]();
  }

  return (
    <StyledLegalRepresentativeInformation
      direction='vertical'
      size={32}
      {...rest}
    >
      <Space
        direction='vertical'
        size='middle'
      >
        <StyledKYCModalContentStepTitle>
          {title}
        </StyledKYCModalContentStepTitle>
        <p>{description}</p>
      </Space>
      <Space
        direction='vertical'
        size='large'
      >
        {/* No tab strip: the rep gets the form only. The invitation and share-link branches
            below are left intact but unreachable, so restoring them is a one-line change. */}
        {selectedComponent}
      </Space>
      <input type='submit' onClick={(e) => (submitEnabled && handleSubmit) && handleSubmit(e)} ref={ref} className='d-none' />
    </StyledLegalRepresentativeInformation>
  );
});

LegalRepresentativeInformation.propTypes = {
  handleUpdateOkEnable: PropTypes.func,
  handleLoadUser: PropTypes.func,
  onFinish: PropTypes.func,
  user: PropTypes.shape({
    id: PropTypes.string
  })
}

export default LegalRepresentativeInformation;
